import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Star } from "lucide-react";
import { QuestionDialog } from "./QuestionDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Tables } from "@/integrations/supabase/types";

type Question = Tables<"questions"> & {
  question_groups?: { name: string } | null;
  job_positions?: { role_name: string }[] | null;
  is_enabled?: boolean;
};

type QuestionGroup = Tables<"question_groups">;
type JobPosition = { id: string; role_name: string };

interface QuestionsTabProps {
  questions: Question[];
  questionGroups: QuestionGroup[];
  jobPositions: JobPosition[];
  loading: boolean;
  onRefetch: () => void;
  canDelete?: boolean;
}

export function QuestionsTab({
  questions,
  questionGroups,
  jobPositions,
  loading,
  onRefetch,
  canDelete = true,
}: QuestionsTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [enabledFilter, setEnabledFilter] = useState<
    "all" | "enabled" | "disabled"
  >("all");
  const { toast } = useToast();


  const handleDelete = async (id: string) => {
    if (!canDelete) return;
    if (!confirm("Are you sure you want to delete this question?")) return;

    const { error } = await supabase.from("questions").delete().eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete question",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Question deleted successfully",
      });
      onRefetch();
    }
  };

  const handleEdit = (question: Question) => {
    setEditingQuestion(question);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingQuestion(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingQuestion(null);
  };

  if (loading) {
    return <div>Loading questions...</div>;
  }

  const filteredQuestions = questions.filter((q) => {
    if (enabledFilter === "enabled") return q.is_enabled !== false;
    if (enabledFilter === "disabled") return q.is_enabled === false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Questions</h2>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Question
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm">Show:</label>
        <select
          className="border rounded p-1 text-sm"
          value={enabledFilter}
          onChange={(e) =>
            setEnabledFilter(e.target.value as "all" | "enabled" | "disabled")
          }
        >
          <option value="all">All</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {questions.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground">No questions found</p>
              <Button onClick={handleCreate} className="mt-4">
                Create your first question
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredQuestions.map((question) => (
            <Card
              key={question.id}
              className={question.is_enabled ? "" : "opacity-50 bg-gray-50"}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Badge variant="secondary">
                        {question.question_type.toUpperCase()}
                      </Badge>
                      {question.is_must_have && (
                        <Badge variant="default" className="bg-yellow-500">
                          <Star className="mr-1 h-3 w-3" />
                          Must-have
                        </Badge>
                      )}
                      {!question.is_enabled && (
                        <Badge variant="destructive">Disabled</Badge>
                      )}
                      <Badge variant="outline">Score: {question.score}</Badge>
                    </div>
                    <CardTitle className="text-lg leading-relaxed">
                      <div className="prose dark:prose-invert max-w-none question-markdown-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {question.question_text}
                        </ReactMarkdown>
                      </div>
                    </CardTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {question.question_groups && (
                        <Badge variant="outline">
                          Group: {question.question_groups.name}
                        </Badge>
                      )}
                      {question.job_position_ids &&
                        question.job_position_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {question.job_position_ids.map((positionId) => {
                              const position = jobPositions.find(
                                (p) => p.id === positionId
                              );
                              return position ? (
                                <Badge key={positionId} variant="outline">
                                  Position: {position.role_name}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(question)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(question.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {question.question_type === "mcq" && question.options && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Options:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(
                        question.options as Record<string, string>
                      ).map(([key, value]) => (
                        <div
                          key={key}
                          className={`p-2 rounded text-sm border ${
                            question.correct_answer === key
                              ? "bg-green-50 border-green-200"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <span className="font-medium">{key}:</span>
                          <div className="prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {value}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {question.question_type === "open_ended" &&
                  question.ai_evaluation_criteria && (
                    <div>
                      <p className="text-sm font-medium mb-2 ">
                        AI Evaluation Criteria:
                      </p>
                      <div className="text-sm text-muted-foreground">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {question.ai_evaluation_criteria}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <QuestionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        editingQuestion={editingQuestion}
        questionGroups={questionGroups}
        jobPositions={jobPositions}
        onSuccess={() => {
          onRefetch();
          handleDialogClose();
        }}
      />
    </div>
  );
}
